/**
 * Logout mutation.
 *
 * Posts to `POST /api/auth/logout` (clears the cookie server-side)
 * and on success sets the `["auth", "me"]` query data directly to
 * `null` rather than invalidating. Setting directly is faster
 * (no refetch) and matches the "we just logged out, we know /me
 * will return 401" semantic — the user is immediately routed to
 * /sign-in by `RequireAuth` once the cache flips.
 *
 * The route change happens automatically: after `useAuth` returns
 * `user: null`, whichever `RequireAuth`-wrapped page the user was
 * on sees `isAuthenticated: false` and issues a `<Navigate>` to
 * /sign-in.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { AUTH_QUERY_KEY } from "@/features/auth/useAuth";

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiFetch<void>("/api/auth/logout", { method: "POST" });
    },
    onSuccess: () => {
      queryClient.setQueryData(AUTH_QUERY_KEY, null);
    },
  });
}
